import "./axxess-intelligence.scss";

import { autoinject, observable, bindable, bindingMode, computedFrom } from "aurelia-framework";
import { Router } from "aurelia-router";
import nameof from "../../../common/nameof";
import {
    IAxxessIntelligenceDataProps,
    IAxxessIntelligenceSearchResponse,
    IAxxessIntelligenceSearchRequest,
    IFeedbackResult,
    IFeedbackOption,
    ILinks,
    IDetailedSearchLinksResult,
    IAxxessIntelligenceUserTracker
} from "../../../interfaces/i-axxess-intelligence";
import { AiService } from "../../../services/axxess-intelligence-service";
import { AuthService } from "../../../services/auth-service";
import { ToastrService } from "./../../../services/toastr-service";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { AdministrativePermissionEnum } from "../../../enums/administrative-permission-enum";
import { PermissionActionEnum } from "../../../enums/permission-action-enum";
import { UserManager } from "oidc-client";
import {
    ValidationController,
    ValidationRules,
    validateTrigger,
    ValidationControllerFactory
} from "aurelia-validation";
import { AxxessIntelligenceStore } from "./axxess-intelligence-store";
import { AxxessIntelligenceThumbEnum } from "../../../enums/axxess-intelligence-thumbs-enum";
import { AiUsageStore } from "../../../common/stores/ai-usage-store";
@autoinject
export class AxxessIntelligence {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    @observable({ changeHandler: nameof<AxxessIntelligence>("searchTermChanged") })
    public searchTerm: string = "";
    private readonly _authService: AuthService;
    private _router: Router;
    private _toastrService: ToastrService;
    private _permissionManager: PermissionManager;
    private _userManager: UserManager;
    public _aiState: AxxessIntelligenceStore;
    public _aiUsageStore: AiUsageStore;
    public hasAskAxxessViewPermission: boolean = false;
    public _AiService: AiService;
    public controller: ValidationController;
    public numberOfResultsToBeShown: number = 10;

    @computedFrom("_aiState.searchTermSync")
    public get searchTermLength(): number {
        return this._aiState.searchTermSync?.length ?? 0;
    }

    public get feedbackOtherOptionText(): string {
        return this._aiState.isFeedbackProperty.feedbackOtherOptionText;
    }
    public set feedbackOtherOptionText(value: string) {
        this._aiState.isFeedbackProperty.feedbackOtherOptionText = value;
    }
    public get searchTermSync(): string {
        return this._aiState.searchTermSync;
    }
    public set searchTermSync(value: string) {
        this.searchTerm = value;
        this._aiState.searchTermSync = value;
    }

    public constructor(
        router: Router,
        _aiService: AiService,
        toastrService: ToastrService,
        permissionManager: PermissionManager,
        userManager: UserManager,
        controllerFactory: ValidationControllerFactory,
        authService: AuthService,
        aiState: AxxessIntelligenceStore,
        aiUsageStore: AiUsageStore
    ) {
        this._router = router;
        this._AiService = _aiService;
        this._permissionManager = permissionManager;
        this._toastrService = toastrService;
        this._userManager = userManager;
        this.controller = controllerFactory.createForCurrentScope();
        this.controller.validateTrigger = validateTrigger.changeOrBlur;
        this._authService = authService;
        this._aiState = aiState;
        this._aiUsageStore = aiUsageStore;
    }

    public async attached() {
        this.initValidation();
        this.getPatientId();
        this.initPermissions();
        this._aiState.accountId = this._authService.getAccountId();
    }

    private initPermissions() {
        this.hasAskAxxessViewPermission = this._permissionManager.checkPermission([
            { resource: AdministrativePermissionEnum.AskAxxess, action: PermissionActionEnum.View }
        ]);
    }

    public searchTermChanged(newValue: string, oldValue: string) {
        if (newValue === oldValue) {
            return;
        }
        this._aiState.isFeedbackProperty.isGivenFeedback = null;
        this._aiState.isFeedbackProperty.isFeedbackSubmitted = false;
        if (this.searchTermLength < 1) {
            this.searchTerm = "";
            this._aiState.isLoading = false;
            this._aiState.isError = false;
            this._aiState.searchCount = 0;
            this._aiState.listOfLinks = [];
            this._aiState.listOfFilters = [];
        } else if (this.searchTermLength >= 1 && this.searchTermLength <= 200) {
            this._aiState.isLoading = true;
            if (this.searchTermLength >= 3) {
                this.debouncedSearch();
            }
        }
    }

    public onInputCloseClicked() {
        this._aiState.searchTermSync = "";
        this._aiState.isError = false;
        this._aiState.isLoading = false;
        this._aiState.isFeedbackProperty.isGivenFeedback = null;
        this._aiState.isFeedbackProperty.isFeedbackSubmitted = false;
        clearTimeout(this._aiState.searchTimeout);
    }

    public async onEnterKeyPress() {
        if (this.searchTerm === this._aiState.lastSearchTerm && this._aiState.searchCount > 0) {
            return;
        }
        if (this.searchTermLength >= 3) {
            this._aiState.isLoading = true;
            await this.onAiSearch();
            this._aiState.lastSearchTerm = this.searchTerm;
        }
    }

    public async handleKeyUp(event: KeyboardEvent) {
        // for non-shift modifiers
        if (event.ctrlKey || event.altKey || event.metaKey || this.isSpecialKey(event)) {
            return;
        } else if (event.key == "Escape") {
            this.onInputCloseClicked();
            return;
        } else if (event.key == "Enter") {
            clearTimeout(this._aiState.searchTimeout);
            this.onEnterKeyPress();
            return;
        }
        if (this.searchTermLength > 200) {
            this._aiState.isLoading = false;
            this._toastrService.error({
                message: `You have exceeded maximum character limit.`,
                title: `Alert`
            });
            return;
        }
        await this.debouncedSearch();
    }

    private isSpecialKey(event: KeyboardEvent): boolean {
        const ignoredKeys = [
            "Shift",
            "Control",
            "Alt",
            "Meta",
            "CapsLock",
            "Escape",
            "Tab",
            "ArrowUp",
            "ArrowDown",
            "ArrowLeft",
            "ArrowRight",
            "PrintScreen",
            "AltGraph"
        ];

        if (String(event.key).toLowerCase() === "s" && event.shiftKey && (event.metaKey || event.ctrlKey)) {
            return true;
        }

        return ignoredKeys.includes(event.key);
    }

    public async debouncedSearch() {
        this._aiState.isLoading = true;
        if (this.searchTermLength >= 3) {
            clearTimeout(this._aiState.searchTimeout);
            this._aiState.searchTimeout = setTimeout(() => {
                this.onAiSearch();
            }, 3000);
        } else if (this.searchTermLength < 1) {
            this._aiState.isLoading = false;
            clearTimeout(this._aiState.searchTimeout);
        }
    }

    public isObjectEmpty = (obj: object) => {
        return Object.keys(obj).length === 0;
    }

    public formatUserInitials = (title: string) => {
        if (title) {
            let name = title.split(" | ")[0].split(" ");
            let firstInitial = name[0] == null ? "" : name[0].trim().substring(0, 1).toLocaleUpperCase();
            let lastInitial =
                name.length > 2
                    ? name[2] == null
                        ? ""
                        : name[2].trim().substring(0, 1).toLocaleUpperCase()
                    : name[1] == null
                    ? ""
                    : name[1].trim().substring(0, 1).toLocaleUpperCase();
            let initials = `${firstInitial}${lastInitial}`;
            return initials;
        } else {
            return null;
        }
    }

    public async isWithinUsageLimitMethod() {
        try {
            const response = await this._AiService.isAxxessFeatureLocked();
            if (response?.status === 200) {
                if (!response.isWithinLimits) {
                    this.onInputCloseClicked();
                }
                this._aiUsageStore.isWithinUsageLimit = response.isWithinLimits;
            }
        } catch (err) {
            console.error(err);
        }
    }

    public async onAiSearch() {
        if (!this._aiUsageStore.isAiPremiumUser) {
            await this.isWithinUsageLimitMethod();
        }
        try {
            this._aiState.isLoading = true;
            this._aiState.isError = false;
            const payload: IAxxessIntelligenceSearchRequest = {
                Term: this.searchTerm,
                QuerySelectionSource: "Quick_Search"
            };
            const response = await this._AiService.getSearchInfo(payload);
            if (response.IsSuccessful) {
                this._aiState.isError = false;
                let result = JSON.parse(response.Content) as IAxxessIntelligenceSearchResponse;
                let linksList = result.Cards.map((item: IAxxessIntelligenceDataProps) => {
                    return item.CardType == "Report" || item.CardType == "Page"
                        ? item.Links.filter((link: ILinks) => link.Title.length > 0 && item.CardType.length > 0)
                              .map((link: ILinks) => ({
                                  ...link,
                                  DisplayName: item.CardType,
                                  Type: item.CardType,
                                  Initials: this.formatUserInitials(item.Data.Title),
                                  PatientId: item.Data.Id,
                                  CardType: item.CardType
                              }))
                              .reduce((acc: ILinks[], val: ILinks) => acc.concat(val), [])
                        : item.CardType == "HelpCenter"
                        ? item.Links.filter((link: ILinks) => link.Title.length > 0 && item.CardType.length > 0).map(
                              (link: ILinks) => ({
                                  ...link,
                                  DisplayName: item.Data.Title,
                                  HighLight: item.Data.Highlights,
                                  Type: item.CardType,
                                  Initials: this.formatUserInitials(item.Data.Title),
                                  PatientId: item.Data.Id,
                                  CardType: item.CardType
                              })
                          )
                        : item.Links.filter((link: ILinks) => link.Title.length > 0 && item.CardType.length > 0)
                              .map((link: ILinks) => ({
                                  ...link,
                                  DisplayName: item.Data.Title,
                                  Type: item.CardType,
                                  Initials: this.formatUserInitials(item.Data.Title),
                                  PatientId: item.Data.Id,
                                  CardType: item.CardType
                              }))
                              .reduce((acc: ILinks[], val: ILinks) => acc.concat(val), []);
                });
                this._aiState.listOfLinks = linksList.reduce((acc, val) => acc.concat(val), []);
                this._aiState.segregatedList = this._aiState.filterableList = this.segregatedByType(
                    this._aiState.listOfLinks
                );
                Object.entries(this._aiState.filterableList).forEach(([key, items]) => {
                    const arr = items as any[];
                    let slicedItems = arr;
                    if (key == "HelpCenter") {
                        slicedItems = arr.slice(0, 10);
                    } else {
                        slicedItems = arr.slice(0, 20);
                    }
                    this._aiState.slicedData[key] = {
                        items: slicedItems,
                        count: arr.length
                    };
                });
                this._aiState.copyFilterableList = new Map(Object.entries(this._aiState.slicedData));
                this._aiState.searchCount = Object.keys(this._aiState.slicedData).reduce(
                    (total: number, key: string) => total + this._aiState.slicedData[key].count,
                    0
                );
                this._aiState.listOfFilters = Object.keys(this._aiState.segregatedList);
                this._aiState.segregatedListFilters = Object.keys(this._aiState.segregatedList);
                this._aiState.isSegregatedListEmpty = this.isObjectEmpty(this._aiState.segregatedList);
                this._aiState.searchTrackingId = result.TrackingId;
            } else {
                this._aiState.isError = true;
                this._aiState.isSegregatedListEmpty = true;
            }
        } catch (err) {
            this._aiState.isError = true;
        } finally {
            this._aiState.isLoading = false;
            this._aiState.slicedData = {};
        }
    }

    public segregatedByType = (data: any) => {
        const groupedByType = data.reduce((acc: any, item: any) => {
            if (!acc[item.Type]) {
                acc[item.Type] = [];
            }
            acc[item.Type].push(item);
            return acc;
        }, {});
        if (groupedByType.Patient) {
            const patientsGrouped = Object.values(
                groupedByType.Patient.reduce((acc: any, item: any) => {
                    if (!acc[item.PatientId]) {
                        acc[item.PatientId] = [];
                    }
                    acc[item.PatientId].push(item);
                    return acc;
                }, {})
            );
            groupedByType.Patient = patientsGrouped;
        }
        return groupedByType;
    }

    public checkTextOverflow(text: string) {
        if (!text) {
            return "";
        }

        if (text.length > 30) {
            return text.slice(0, 30).concat("...");
        }
        return text;
    }

    public checkLinkOverflowHelpCenter(text: string) {
        if (!text) {
            return "";
        }

        if (text.length > 75) {
            return text.slice(0, 75).concat("...");
        }
        return text;
    }

    public initValidation() {
        ValidationRules.ensure((x: AxxessIntelligence) => x.feedbackOtherOptionText)
            .required()
            .withMessage("Please enter details before submitting.")
            .on(this);
    }

    public feedbackResult(feedback: string) {
        if (feedback == this._aiState.isFeedbackProperty.isGivenFeedback) {
            this._aiState.isFeedbackProperty.isGivenFeedback = null;
            return;
        }
        if (feedback == "Negative") {
            this._aiState.isFeedbackProperty.isGivenFeedback = "Negative";
        } else {
            this._aiState.isFeedbackProperty.isGivenFeedback = "Positive";
        }
        this.resetFeedback();
    }

    public toggleOption(selection: IFeedbackOption): void {
        if (selection.name == "Other") {
            this.feedbackOtherOptionText = "";
            this.controller.reset();
        }
        if (this._aiState.isFeedbackProperty.isGivenFeedback == "Positive") {
            this._aiState.isFeedbackProperty.selectedFeedbackItems = this._aiState.isFeedbackProperty.positiveFeedbackOptions
                .filter((item) => item.isSelected)
                .map((item) => item.name);
        } else {
            this._aiState.isFeedbackProperty.selectedFeedbackItems = this._aiState.isFeedbackProperty.negativeFeedbackOptions
                .filter((item) => item.isSelected)
                .map((item) => item.name);
        }
    }

    public async getPatientId() {
        let user = await this._userManager.getUser();
        this._aiState.patientId = user.profile.sub;
    }

    public async cancelFeedback() {
        try {
            const payload: IFeedbackResult = {
                trackingId: this._aiState.searchTrackingId,
                thumb: !this._aiState.isFeedbackProperty.isGivenFeedback
                    ? AxxessIntelligenceThumbEnum.None
                    : this._aiState.isFeedbackProperty.isGivenFeedback == "Positive"
                    ? AxxessIntelligenceThumbEnum.Up
                    : AxxessIntelligenceThumbEnum.Down,
                selectedOptions: this._aiState.isFeedbackProperty.selectedFeedbackItems,
                otherText: this._aiState.isFeedbackProperty.selectedFeedbackItems.includes("Other")
                    ? this.feedbackOtherOptionText
                    : null,
                isFeedbackSubmitted: false,
                userId: this._aiState.patientId,
                agencyId: this._aiState.accountId,
                applicationId: 256
            };
            await this._AiService.feedbackResult(payload);
        } catch (err) {
            console.log(err);
        } finally {
            this.resetFeedback();
            this._aiState.isFeedbackProperty.isGivenFeedback = null;
        }
    }

    public async submitFeedback() {
        let res = await this.controller?.validate();
        if (!res.valid) {
            return;
        }
        if (this._aiState.selectedFeedbackItemsLength == 0) {
            this._toastrService.error({
                message: `Please select at least one option.`,
                title: `Alert`
            });
            return;
        }
        try {
            const payload: IFeedbackResult = {
                trackingId: this._aiState.searchTrackingId,
                thumb: !this._aiState.isFeedbackProperty.isGivenFeedback
                    ? AxxessIntelligenceThumbEnum.None
                    : this._aiState.isFeedbackProperty.isGivenFeedback == "Positive"
                    ? AxxessIntelligenceThumbEnum.Up
                    : AxxessIntelligenceThumbEnum.Down,
                selectedOptions: this._aiState.isFeedbackProperty.selectedFeedbackItems,
                otherText: this._aiState.isFeedbackProperty.selectedFeedbackItems.includes("Other")
                    ? this.feedbackOtherOptionText
                    : null,
                isFeedbackSubmitted: true,
                userId: this._aiState.patientId,
                agencyId: this._aiState.accountId,
                applicationId: 256
            };
            await this._AiService.feedbackResult(payload);
            this._aiState.isFeedbackProperty.isFeedbackSubmitted = true;
            this.closeFeedbackMessageWithDelay();
        } catch (err) {
            console.log(err);
        } finally {
            this.resetFeedback();
            this._aiState.isFeedbackProperty.isGivenFeedback = null;
        }
    }

    public closeFeedbackMessageWithDelay() {
        if (this._aiState.isFeedbackProperty.isFeedbackSubmitted) {
            setTimeout(() => {
                this._aiState.isFeedbackProperty.isFeedbackSubmitted = false;
            }, 3000);
        }
    }

    public resetFeedback() {
        this._aiState.resetFeedback();
        this.controller.reset();
    }

    public closeFeedbackMessage() {
        this._aiState.isFeedbackProperty.isFeedbackSubmitted = false;
    }

    public expandedSearch() {
        this.trackUserActivity({
            SelectedResultCategory: "View_All",
            SelectedResultIndex: null,
            SelectedResultTitle: "",
            ExitedWithoutInteraction: false,
            TrackingId: this._aiState.searchTrackingId
        });

        this._router.navigate(`/axxess-intelligence/axxess-intelligence-page?term=${this.searchTerm}`);
        this.onInputCloseClicked();
    }

    public viewTypeResults(type: string) {
        this._router.navigate(`/axxess-intelligence/axxess-intelligence-page?term=${this.searchTerm}&filter=${type}`);
        this.onInputCloseClicked();
    }

    public checkTextOverflowHelpCenter(text: string, highLight: string[]) {
        if (!text) {
            return "";
        }
        if (text.length > 0) {
            if (highLight.length > 0) {
                const escaped = highLight.map((word) => word.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&"));
                const regex = new RegExp(`(${escaped.join("|")})`, "gi");
                const longText = text.replace(regex, (match) => `<b>${match}</b>`);
                if (text.length > 150) {
                    const trimText = longText.slice(0, 150).concat("...");
                    return trimText;
                }
                return longText;
            } else if (text.length > 150) {
                return text.slice(0, 150).concat("...");
            }
        }
        return text;
    }

    public openSearchedPage(links: IDetailedSearchLinksResult, event?: Event) {
        if (!links?.Url) {
            return;
        }

        if (links.CardType == "HelpCenter") {
            window.open(links.Url, "_blank");
            return;
        }

        this.trackUserActivity({
            SelectedResultCategory: links.Type,
            SelectedResultIndex: null,
            SelectedResultTitle: `${links.Title} | ${links.DisplayName}`,
            ExitedWithoutInteraction: false,
            TrackingId: this._aiState.searchTrackingId
        });

        event?.stopPropagation();
        event?.preventDefault();
        const currentFragment = this._router.currentInstruction?.fragment;
        const constructUrl = `/${links.Url.split("?")[0]}`;

        if (currentFragment === constructUrl) {
            this._toastrService.info({
                title: `Info`,
                message: `You already have the page open. Please close that window and try again.`
            });
            this.onInputCloseClicked();
            return;
        }
        this.onInputCloseClicked();
        this._router.navigate(links.Url);
    }

    public async trackUserActivity(params: IAxxessIntelligenceUserTracker) {
        try {
            await this._AiService.trackUserActions({ ...params });
        } catch (err) {
            console.log(err);
        }
    }

    public detached() {
        this.feedbackOtherOptionText = "";
    }
}
